import React, { useState, useEffect, useRef } from 'react';
import { db, storage, auth } from "../firebase"; // make sure auth is exported from firebase config
import { signOut } from "firebase/auth";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function AdminDashboard() {
  const [blogs, setBlogs] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);

  const blogsRef = collection(db, "blogs");
  const formRef = useRef(null);

  const fetchBlogs = async () => {
    const snapshot = await getDocs(blogsRef);
    setBlogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const uploadImage = async (file) => {
    const fileRef = ref(storage, `blogs/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const nameToUrl = (title) => 
    title
      ?.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleSave = async () => {
    try {
      let imageUrl = null;
      if (file) {
        imageUrl = await uploadImage(file);
      }

      const urlKey = nameToUrl(title);

      if (editId) {
        const blogDocRef = doc(db, "blogs", editId);
        const existingDoc = await getDoc(blogDocRef);

        await updateDoc(blogDocRef, {
          title,
          content,
          urlKey,
          ...(imageUrl && { imageUrl }),
          createdAt: existingDoc.exists() ? existingDoc.data().createdAt : serverTimestamp()
        });
        setEditId(null);
      } else {
        await addDoc(blogsRef, {
          title,
          content,
          urlKey,
          imageUrl: imageUrl || "",
          createdAt: serverTimestamp()
        });
      }

      setTitle("");
      setContent("");
      setFile(null);
      fetchBlogs();
    } catch (error) {
      console.error("Error saving blog:", error);
      alert(error.message);
    }
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "blogs", id));
    fetchBlogs();
  };

  const handleEdit = (blog) => {
    setEditId(blog.id);
    setTitle(blog.title);
    setContent(blog.content);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/adminlogin"; // redirect to login page
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header with title + logout */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Eyecare Blog Dashboard</h2>
        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-4 py-2 rounded"
        >
          Logout
        </button>
      </div>

      {/* Blog form */}
      <div ref={formRef} className="bg-white p-4 shadow rounded mb-6">
        <input
          type="text"
          placeholder="Blog Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border p-2 w-full mb-3 rounded"
        />
        <ReactQuill
          value={content}
          onChange={setContent}
          placeholder="Write your blog content here..."
          className="mb-3 bg-white rounded"
          modules={{
            toolbar: [
              [{ 'header': [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              ['link'],
              ['clean']
            ]
          }}
          formats={[
            'header',
            'bold', 'italic', 'underline', 'strike',
            'list', 'bullet',
            'link', 'image'
          ]}
        />
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          className="mb-3"
        />
        <button
          onClick={handleSave}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          {editId ? "Update Blog" : "Add Blog"}
        </button>
      </div>

      {/* Blog list */}
      <div>
        {blogs.map((blog) => (
          <div key={blog.id} className="bg-white shadow p-4 mb-4 rounded">
            {blog.imageUrl && (
              <img
                src={blog.imageUrl}
                alt={blog.title}
                className="w-100 h-40 object-cover mb-3 rounded"
              />
            )}
            <h3 className="text-xl font-bold">{blog.title}</h3>
            <div
              className="prose max-w-none line-clamp-4"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => handleEdit(blog)}
                className="bg-yellow-500 text-white px-3 py-1 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(blog.id)}
                className="bg-red-500 text-white px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
